import { ChangeEvent, useCallback, FocusEvent } from 'react'
import { InputBase, InputLabel } from '@mui/material'
import { Box } from '@mui/system'
import classNames from 'classnames'
import { useField, useFormikContext } from 'formik'
import { LfCloseIcon, LfCoinIcon } from 'src/components/shared/ui/Icons'
import { fieldsStyles as styles } from 'src/components/shared/ui/form/fields/fieldsStyles'
import { EscritosFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'

type Props = {
  id: string
  label?: string
  readOnly?: boolean
  required?: boolean
  maxLength?: number
  disabled?: boolean
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  dataAttribute?: string
  forceOnChangeValidation?: boolean
  clearable?: boolean
  currencyType?: boolean
  type?: string
  spaceLabel?: boolean
  spaceError?: boolean
  sx?: any
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void
}

function InputField({
  id,
  label,
  readOnly = false,
  required = false,
  type = 'text',
  maxLength,
  disabled = false,
  onChange = _e => null,
  dataAttribute,
  forceOnChangeValidation = false,
  clearable = true,
  currencyType = false,
  spaceLabel = true,
  spaceError = true,
  sx = {},
  onBlur = _e => null,
  onFocus = _e => null,
  onPaste = _e => null
}: Props): JSX.Element {
  const [fieldInfo, meta] = useField(id)
  const { setFieldValue, setFieldTouched } = useFormikContext<EscritosFormValues>()

  const inputClassNames = classNames({
    disabled,
    required,
    error: meta.touched && meta.error,
    clearable
  })

  function showLabel() {
    if (label) {
      return (
        <InputLabel id={`${id}-label`} className={inputClassNames}>
          {label}
        </InputLabel>
      )
    }

    if (spaceLabel) {
      return <Box component="span" sx={styles.blankSpace} />
    }

    return <></>
  }

  const onChangeCallback = useCallback(
    e => {
      const { value } = e.target
      setFieldValue(id, value, true)
      if (!value) {
        setFieldTouched(id, true, false)
      }
      onChange(e)
      return fieldInfo.onChange
    },
    [fieldInfo, id, onChange, setFieldValue, setFieldTouched]
  )

  return (
    <Box sx={sx}>
      {showLabel()}
      <InputBase
        id={id}
        readOnly={readOnly}
        sx={styles.input}
        className={inputClassNames}
        inputProps={{
          maxLength,
          'data-attribute': dataAttribute
        }}
        type={type}
        onChange={forceOnChangeValidation ? fieldInfo.onChange : onChangeCallback}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          onBlur(e)
          fieldInfo.onBlur(e)
        }}
        onFocus={onFocus}
        onPaste={onPaste}
        value={fieldInfo.value ? fieldInfo.value : ''}
        disabled={disabled}
        endAdornment={
          <>
            {clearable && !readOnly && fieldInfo.value ? (
              <LfCloseIcon
                onClick={() => {
                  setFieldValue(id, '', true)
                  setFieldTouched(id, true, false)
                }}
                className="iconClean"
              />
            ) : null}
          </>
        }
        startAdornment={
          currencyType && (
            <Box
              className="wrapperIconCoin"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <LfCoinIcon />
            </Box>
          )
        }
      />
      {spaceError && (
        <Box component="span" sx={styles.helpText}>
          {meta.touched ? meta.error : null}
        </Box>
      )}
    </Box>
  )
}

export default InputField
